import { GenericAvailabilityType, Maybe, ProductTypeProps } from '.'
import EnergyLabel from './product-card-energyLabel'
import { Props as FavoritePropType } from '../product-add-to-favorites/product-add-to-favorites'
import ProductAvailability from '../product-availability'
import ProductBadge from '../product-badge'
import { PriceType } from '../product-badge/product-badge'
import Picture from './product-card-picture'
import Title from './product-card-title'
import { CompareButton } from '../product-compare/product-compare'
import ProductPrice from '../product-price'
import ReviewStars from '../review-stars'
import PromotedSpecification from './product-card-specifications'
import { Props } from './product-card-gallery'
import classNames from 'classnames'
import { FavoriteButton } from '../product-add-to-favorites/favorites-button'
import Container from './product-card-container'
import ToProductButton from './product-card-toProductButton'

type WrapperProps = {
  cardClasses?: Props['cardClasses']
  showBadge?: boolean
  withBorder?: Props['withBorder']
  children: React.ReactNode
  withTheme?: boolean
  withSideButtons?: boolean
}

const Wrapper = ({
  cardClasses,
  showBadge,
  withBorder,
  children,
  withTheme,
  withSideButtons,
}: WrapperProps) => (
  <section
    className={classNames(cardClasses, 'c-product-card c-product-card--gallery', {
      'c-product-card--gallery--show-badge': showBadge,
      'u-border u-border-light': withBorder,
      't-nullify': !withTheme,
      ' flex-direction-column': withSideButtons,
    })}>
    {children}
  </section>
)

type SpecificationListProps = {
  slim: Props['slim']
  list: Props['product']['promotedSpecifications']
}

const SpecificationList = ({ slim, list }: SpecificationListProps) => {
  if (slim) {
    return null
  }
  return (
    <ul className="c-product-card-list">
      {list?.map((spec, idx) => (
        <PromotedSpecification key={idx} spec={spec} />
      ))}
    </ul>
  )
}

const Content = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={classNames('c-product-card-content', className)}>{children}</div>
)

const Top = ({ className, children }: { children: React.ReactNode; className?: string }) => (
  <div className={classNames('c-product-card-content__top', className)}>{children}</div>
)

const Center = ({ children }: { children: React.ReactNode }) => <div>{children}</div>

const Stars = ({
  reviewScore,
  reviewCount,
  className,
}: {
  reviewScore: number
  reviewCount: number
  className?: string
}) => <ReviewStars reviewScore={reviewScore} className={className} reviewCount={reviewCount} />

type BadgeProps = {
  isAvailableForSale: boolean | undefined
  price?: Props['product']['price']
  isNewProduct: Props['product']['isNewProduct']
  isCampaignProduct?: boolean
  description?: string
}
const Badge = ({
  isAvailableForSale,
  price,
  isNewProduct,
  isCampaignProduct,
  description,
}: BadgeProps) => {
  if (!isAvailableForSale) {
    return null
  }
  return (
    <ProductBadge
      price={price}
      isNewProduct={isNewProduct}
      isCampaignProduct={isCampaignProduct}
      description={description}
    />
  )
}

type PriceProps = {
  isAvailableForSale: boolean | undefined
  price: Props['product']['price']
  displayVatInfo?: boolean
  className?: string
  priceClassName?: string
  stripped?: boolean
}

const Price = ({
  isAvailableForSale,
  displayVatInfo = true,
  className = 'c-product-card-price--row',
  ...rest
}: PriceProps) => {
  if (!isAvailableForSale) {
    return null
  }
  return (
    <ProductPrice
      displayVatInfo={displayVatInfo}
      className={classNames('c-product-card-price', className)}
      {...rest}
    />
  )
}

const ButtonRow = ({ children }: { children: React.ReactNode }) => (
  <div className={'c-product-card__btn-row'}>{children}</div>
)

type FavoritesProps = {
  show?: boolean
}

const Favorites = ({ show = true, square = true, ...other }: FavoritesProps & FavoritePropType) => {
  if (!show) {
    return null
  }
  return <FavoriteButton square={square} {...other} />
}

type ActionRowProps = {
  showActionRow?: Props['showActionRow']
  children: React.ReactNode
  classes?: string
}

const ActionRow = ({ showActionRow = true, children, classes }: ActionRowProps) => {
  if (!showActionRow) return null
  return <div className={classNames('c-product-card__action-row gap-12', classes)}>{children}</div>
}

type AvailabilityProps = {
  availability?: Maybe<GenericAvailabilityType>
  isBid?: PriceType['isBid']
  showOnlyIcon?: boolean
  listForm?: boolean
  className?: string
  defaultContainerClassName?: string
  compactStatus?: boolean
  containerClassNames?: string
}
const Availability = ({ listForm = true, compactStatus = false, ...props }: AvailabilityProps) => {
  if (!props.availability) {
    return null
  }
  return <ProductAvailability listForm={listForm} compactStatus={compactStatus} {...props} />
}

type CompareProps = {
  id: ProductTypeProps['id']
  displayName: ProductTypeProps['displayName']
  categoryName: ProductTypeProps['categoryName']
}

const Compare = ({ id, displayName = '', categoryName }: CompareProps) => {
  if (!categoryName) {
    return null
  }
  return (
    <CompareButton productId={id} displayName={displayName} categoryName={categoryName} listForm />
  )
}

const Card = () => null
Card.Container = Container
Card.Wrapper = Wrapper
Card.SpecificationList = SpecificationList
Card.Picture = Picture
Card.Content = Content
Card.Top = Top
Card.Center = Center
Card.Stars = Stars
Card.Badge = Badge
Card.Energy = EnergyLabel
Card.Price = Price
Card.ButtonRow = ButtonRow
Card.ToProductButton = ToProductButton
Card.Title = Title
Card.Favorites = Favorites
Card.ActionRow = ActionRow
Card.Availability = Availability
Card.Compare = Compare

export { Card }
